<template>
    <v-container fluid>
        <v-row v-if="troubleshootingUpload" class="mt-3">
            <v-col md="3">
                <h4 class="mb-2">Submitted <time-ago-with-date :date="troubleshootingUpload?.createdAt" /></h4>
                <user-chip :user="troubleshootingUpload.user" background-color="white" />
            </v-col>
        </v-row>
        <v-row v-if="troubleshootingUpload">
            <v-col>
                <h4 class="mb-2">Device Info</h4>
                <v-table>
                    <tbody>
                        <tr>
                            <th>App Version</th>
                            <td>{{ troubleshootingUpload?.data.appVersion }}</td>
                        </tr>
                        <tr>
                            <th>Device ID</th>
                            <td>{{ troubleshootingUpload?.data.deviceInfo.deviceId ?? troubleshootingUpload?.data.deviceInfo.uuid }}</td>
                        </tr>
                        <tr>
                            <th>Device Info</th>
                            <td>
                                {{ troubleshootingUpload?.data.deviceInfo.manufacturer }} {{ troubleshootingUpload?.data.deviceInfo.model }} "{{
                                    troubleshootingUpload?.data.deviceInfo.name
                                }}"
                            </td>
                        </tr>
                        <tr>
                            <th>Device OS</th>
                            <td>{{ troubleshootingUpload?.data.deviceInfo.operatingSystem }} {{ troubleshootingUpload?.data.deviceInfo.osVersion }}</td>
                        </tr>
                        <tr>
                            <th>Push Notifications</th>
                            <td>{{ troubleshootingUpload?.data.deviceInfo.pushNotificationsEnabled }}</td>
                        </tr>
                        <tr>
                            <th>Device Locale</th>
                            <td>{{ troubleshootingUpload?.data.deviceInfo.locale }}</td>
                        </tr>
                    </tbody>
                </v-table>
            </v-col>
            <v-col>
                <h4 class="mb-2">Token Info</h4>
                <v-table>
                    <tbody>
                        <tr v-if="troubleshootingUpload?.data.tokenInfo.idTokenIssuedAt">
                            <th>ID Token Issued</th>
                            <td><time-ago-with-date :date="troubleshootingUpload?.data.tokenInfo.idTokenIssuedAt" /></td>
                        </tr>
                        <tr v-if="troubleshootingUpload?.data.tokenInfo.idTokenExpiresAt">
                            <th>ID Token Expires</th>
                            <td><time-ago-with-date :date="troubleshootingUpload?.data.tokenInfo.idTokenExpiresAt" /></td>
                        </tr>
                        <tr v-if="troubleshootingUpload?.data.tokenInfo.accessTokenIssuedAt">
                            <th>Access Token Issued</th>
                            <td><time-ago-with-date :date="troubleshootingUpload?.data.tokenInfo.accessTokenIssuedAt" /></td>
                        </tr>
                        <tr v-if="troubleshootingUpload?.data.tokenInfo.accessTokenExpiresAt">
                            <th>Access Token Expires</th>
                            <td><time-ago-with-date :date="troubleshootingUpload?.data.tokenInfo.accessTokenExpiresAt" /></td>
                        </tr>
                        <tr>
                            <th>Has Refresh Token</th>
                            <td>{{ troubleshootingUpload?.data.tokenInfo.hasRefreshToken }}</td>
                        </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>
        <v-row v-if="troubleshootingUpload?.data.debugLogs && troubleshootingUpload?.data.debugLogs.length > 0">
            <v-col>
                <h4 class="mb-2 mt-4">Debug Logs <small>(pinia store only)</small></h4>
                <pre>
                    <div v-for="(line, index) of troubleshootingUpload?.data.debugLogs" :key="index" :Class="classesForLogLine(line)">{{ line }}</div>
                </pre>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup lang="ts">
import { useApi } from '@/api'
import '@/components/VesselDisc.vue'
import { handleApiError } from '@/lib/utils'
import { useBreadCrumbsStore } from '@/stores/breadCrumbs'
import type { TroubleshootingUploadV1 } from '@geckoal/gecko-api-client'
import { onMounted, ref, watch } from 'vue'
import TimeAgoWithDate from '../../components/TimeAgoWithDate.vue'
import UserChip from '../../components/users/UserChip.vue'

const breadCrumbsStore = useBreadCrumbsStore()

const props = defineProps({
    accountId: {
        type: Number,
        required: true
    },
    uploadId: {
        type: Number,
        required: true
    }
})

onMounted(() => {
    _loadInfo()
})

watch([() => props.accountId, () => props.uploadId], () => {
    _loadInfo()
})

const loadingTroubleshootingUploads = ref(false)
const troubleshootingUpload = ref<TroubleshootingUploadV1 | undefined>()

const getAccountTroubleshootingUploads = async () => {
    await handleApiError(
        async () => {
            loadingTroubleshootingUploads.value = true
            const result = await useApi().getAccountTroubleshootingUploadV1({
                accountId: props.accountId,
                uploadId: props.uploadId
            })
            troubleshootingUpload.value = result.upload
        },
        async () => {
            loadingTroubleshootingUploads.value = false
        }
    )
}

async function _loadInfo() {
    _updateBreadCrumb()
    await getAccountTroubleshootingUploads()
    _updateBreadCrumb()
}

function _updateBreadCrumb() {
    breadCrumbsStore.$patch({
        items: [
            {
                text: `Account #${props.accountId}`,
                to: {
                    name: 'AccountDetails',
                    params: {
                        accountId: props.accountId
                    }
                }
            },
            {
                text: `Troubleshooting Uploads`,
                to: {
                    name: 'AccountTroubleShootingUploads',
                    params: {
                        accountId: props.accountId
                    }
                }
            },
            {
                text: `Upload #${troubleshootingUpload.value?.uploadId ?? 'loading'}`
            }
        ]
    })
}

const classesForLogLine = (line: string) => {
    if (line.indexOf('=== FRESH BOOT') > -1) return ['freshBoot']
    if (line.toLowerCase().indexOf('error') > -1) return ['errorLine']
    return undefined
}
</script>

<style lang="scss" scoped>
pre {
    padding: 10px;
    background-color: white;
    margin-top: 20px;
    max-width: 100%;
    white-space: pre-wrap;
    word-wrap: break-word;
}

.freshBoot {
    background-color: rgba(0, 200, 0, 0.2);
    margin-top: 50px;
}

.errorLine {
    background-color: rgba(200, 0, 0, 0.1);
}
</style>
